var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { format, parse } from 'date-fns';
import { useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { ENTER_KEY } from '../../../const/keyNames';
import DatePickerSelector from './DatePickerSelector';
import { validDateFormat } from './DatePickerTextfield/helpers';
import DatePickerTextfield, { dateFormat } from './DatePickerTextfield/index';
import { CalendarIcon, CalenderIconContainer, SelectButton } from './subcomponents';
// 'shouldCalenderDateBeSet' is to validate if the date is empty/null and  is in range
const shouldCalenderDateBeSet = (date, parsedDate, startDate, endDate) => {
    const isDateAfterStartDate = startDate && parsedDate >= startDate;
    const isDateBeforeEndDate = endDate && parsedDate <= endDate;
    if (date) {
        if (!startDate && !endDate) {
            return true;
        }
        if (isDateAfterStartDate && isDateBeforeEndDate) {
            return true;
        }
        if (isDateAfterStartDate && !endDate) {
            return true;
        }
        if (isDateBeforeEndDate && !startDate) {
            return true;
        }
    }
    return false;
};
const isValidDate = (date) => date && !Number.isNaN(date.getTime());
const SubmitDateButton = ({ shouldSubmitOnCalendarDateClicked, selectedDate, temporarySelectedDate, submitButtonCustomText, handleDateSubmit }) => !shouldSubmitOnCalendarDateClicked && (_jsx(SelectButton, Object.assign({ buttonType: "ghost", onClick: handleDateSubmit, disabled: !selectedDate && !temporarySelectedDate }, { children: submitButtonCustomText }), "submit button"));
const DatePicker = (_a) => {
    var { className, startDate, endDate, excludeDates, selectedDate, handleOnChange = () => { }, error, startDateErrorMessage, endDateErrorMessage, excludeDatesErrorMessage, placeholderLabel, idTextField = 'custom-input', isReadOnly, dataAnalytics, dataObject, dataReason, dataType, required, helpText, calendarIcon, calendarDateClickMode = 'select', submitButtonCustomText = 'select date', onBlurHandler, onModalChange, onTextInputChange } = _a, props = __rest(_a, ["className", "startDate", "endDate", "excludeDates", "selectedDate", "handleOnChange", "error", "startDateErrorMessage", "endDateErrorMessage", "excludeDatesErrorMessage", "placeholderLabel", "idTextField", "isReadOnly", "dataAnalytics", "dataObject", "dataReason", "dataType", "required", "helpText", "calendarIcon", "calendarDateClickMode", "submitButtonCustomText", "onBlurHandler", "onModalChange", "onTextInputChange"]);
    const [temporarySelectedDate, setTemporarySelectedDate] = useState(selectedDate);
    const [shouldRenderDatePicker, setShouldRenderDatePicker] = useState(false);
    const calenderIconRef = useRef();
    const shouldSubmitOnCalendarDateClicked = calendarDateClickMode === 'submit';
    const isSelectedDateValid = isValidDate(selectedDate);
    const handleDateTextFieldKeyDown = (date) => {
        // validate the entered date matches the exact format we are looking for "MM/dd/yyyy"
        if (!validDateFormat(date)) {
            handleOnChange(new Date('Invalid Date'));
            return;
        }
        // 'parsedDate' converts the date into desired format which is 'MM/dd/yyyy'
        const parsedDate = parse(date, dateFormat, new Date());
        const shouldSetCalenderDate = shouldCalenderDateBeSet(date, parsedDate, startDate, endDate);
        if (shouldSetCalenderDate) {
            setTemporarySelectedDate(parsedDate);
        }
        handleOnChange(parsedDate);
    };
    const handleDateTextFieldChange = ({ date, errorMessage, value }) => {
        const parsedDate = parse(date, dateFormat, new Date());
        onTextInputChange === null || onTextInputChange === void 0 ? void 0 : onTextInputChange({
            date: parsedDate,
            errorMessage,
            value
        });
    };
    const handleCalendarModalOpen = () => {
        setShouldRenderDatePicker(true);
        if (typeof onModalChange === 'function') {
            // Trigger optional callback
            onModalChange(true);
        }
    };
    const handleCalendarModalClose = () => {
        // shift focus on the calendar icon
        if (calenderIconRef.current) {
            const calenderIcon = calenderIconRef.current;
            calenderIcon.focus();
        }
        // Close the calendar modal
        setShouldRenderDatePicker(false);
        if (typeof onModalChange === 'function') {
            // Trigger optional callback
            onModalChange(false);
        }
    };
    // handles the calendar date input
    const handleSelectedDateChange = (date) => {
        if (shouldSubmitOnCalendarDateClicked) {
            handleOnChange(date);
            handleCalendarModalClose();
            return;
        }
        setTemporarySelectedDate(date);
    };
    const handleDateSubmit = () => {
        handleOnChange(temporarySelectedDate);
        handleCalendarModalClose();
    };
    const handleCalendarIconKeyDown = (e) => {
        if (e.key === ENTER_KEY) {
            handleCalendarModalOpen();
        }
    };
    const handleCalendarIconClick = () => handleCalendarModalOpen();
    const calendarProps = Object.assign({ className,
        isSelectedDateValid,
        shouldSubmitOnCalendarDateClicked,
        selectedDate,
        temporarySelectedDate,
        setTemporarySelectedDate,
        setShouldRenderDatePicker,
        excludeDates,
        handleSelectedDateChange,
        startDate,
        endDate,
        required }, props);
    const commonModalProps = {
        'aria-labelledby': 'datepicker wrapper',
        footerButtons: [
            _jsx(SubmitDateButton, { handleDateSubmit: handleDateSubmit, submitButtonCustomText: submitButtonCustomText, shouldSubmitOnCalendarDateClicked: shouldSubmitOnCalendarDateClicked, selectedDate: selectedDate, temporarySelectedDate: temporarySelectedDate }, "date-submit-button")
        ],
        id: 'datepicker-wrapper',
        isOpen: shouldRenderDatePicker
    };
    const datePickerTextfieldProps = {
        idTextField,
        onBlurHandler,
        placeholderLabel,
        helpText,
        inputDate: isSelectedDateValid ? format(selectedDate, dateFormat) : '',
        minDate: startDate,
        maxDate: endDate,
        excludeDates,
        startDateErrorMessage,
        endDateErrorMessage,
        excludeDatesErrorMessage,
        error,
        required,
        rightIcon: (
        /**
         * NOTE: due to accessibility concerns and how the Textfield component applies styles to this rightIcon component,
         * this div needs to be set with `role=button` despite the warning from sonarqube. the suggested `input` and `button` tags will break
         * this component if used with the Textfield
         */
        _jsx(CalenderIconContainer, Object.assign({ ref: calenderIconRef, role: "button", tabIndex: 0, "aria-label": "date picker", onClick: handleCalendarIconClick, onKeyDown: handleCalendarIconKeyDown }, { children: calendarIcon !== null && calendarIcon !== void 0 ? calendarIcon : (_jsx(CalendarIcon, { size: "2x", icon: error ? ['fal', 'calendar-exclamation'] : ['fas', 'calendar-alt'] })) }))),
        handleDateTextFieldChange,
        handleDateTextFieldKeyDown,
        isReadOnly,
        dataObject,
        dataReason,
        dataType,
        dataAnalytics
    };
    return (_jsxs(_Fragment, { children: [_jsx(DatePickerTextfield, Object.assign({}, datePickerTextfieldProps)), _jsx(DatePickerSelector, { calendarProps: calendarProps, commonModalProps: commonModalProps, handleCalendarModalClose: handleCalendarModalClose })] }));
};
export default DatePicker;
